// src/utils/reloadPage.js
// Reloads the app, at most once, whatever asks.
//
// Used after nobody is signed in any more on a launch that started Firebase without
// Google's popup helper (utils/lastRole.js decides when), so the login page's
// Continue With Google button has the helper again. Its own module so tests can
// count reloads without touching the rest of the sign-in notes.
//
// Two guards against reloading over and over (the second added in review, same day).
// The first is lastRole.js: it asks for a reload only once the signed-in-before note
// is confirmed gone, so the reloaded app starts with the helper. The second, here,
// does not depend on that note: the time is written into this tab's session storage
// before reloading, and a reload asked for within a minute of the last one is
// skipped. A tab that cannot write it is never reloaded. The login page is already
// showing either way; only Continue With Google may need a second tap.
//
// A message the person must see (their account was deleted or disabled) would go
// with the old page, so it is kept in session storage for the reloaded page, which
// shows it once (takeReloadNotice, App.js).
//
// Fifth review round (09/16/2026). The Retry button on the "sign-in could not be
// checked" screen reloads the app too (App.js). Sharing one stamp meant a Sign Out
// soon after a Retry reload got no reload at all, and Continue with Google then
// failed on its first tap. So each kind of reload names its own stamp (`key`), and
// each is still limited to once a minute. The sign-out reload keeps the old stamp.

export const RELOAD_AT_KEY = "tes:signInReloadAt";
export const RETRY_RELOAD_AT_KEY = "tes:retryReloadAt";
export const RELOAD_NOTICE_KEY = "tes:signInReloadNotice";
export const RELOAD_GUARD_MS = 60 * 1000;

let reloadRequested = false;

// Some browsers throw the moment session storage is touched (site data blocked).
function sessionStore(win) {
  try {
    return win?.sessionStorage || null;
  } catch {
    return null;
  }
}

// Returns true when the browser was asked to reload. `key` is the stamp this kind of reload is limited by.
export function reloadPage({ win = window, notice = null, now = Date.now(), key = RELOAD_AT_KEY } = {}) {
  if (reloadRequested) return false;
  const storage = sessionStore(win);
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(key));
    if (last && Math.abs(now - last) < RELOAD_GUARD_MS) return false;
    const stamp = String(now);
    storage.setItem(key, stamp);
    // Only a time that is really there can stop the next page reloading.
    if (storage.getItem(key) !== stamp) return false;
  } catch {
    return false;
  }
  if (notice) {
    try {
      storage.setItem(RELOAD_NOTICE_KEY, String(notice));
    } catch {
      // The reload still happens; only the message is lost.
    }
  }
  reloadRequested = true;
  try {
    win.location.reload();
  } catch {
    // The login page is already showing; only the Google button may need a second tap.
  }
  return true;
}

// The message kept for this page by the reload, if any. Removed as it is read, so it
// shows only once.
export function takeReloadNotice(win = window) {
  const storage = sessionStore(win);
  if (!storage) return null;
  try {
    const notice = storage.getItem(RELOAD_NOTICE_KEY);
    if (notice === null) return null;
    storage.removeItem(RELOAD_NOTICE_KEY);
    return notice || null;
  } catch {
    return null;
  }
}
